/**
 * Corporate-event and news markers along the bottom of the price pane.
 *
 * Anchored to timestamps and re-projected on every pan and zoom, the same way
 * drawings are. They matter more than decoration: a gap that reads as a
 * breakout is very often an earnings reaction, and the step down on an
 * ex-dividend date is not a sell-off.
 */
import { EVENT_COLOR, type Bar, type MarketEvent, onColor } from "@tt/shared";
import type { IChartApi, Time } from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";

const ROW_HEIGHT = 20;

export function EventMarkers({
  chart, bars, events, enabled,
}: {
  chart: IChartApi | null;
  bars: Bar[];
  events: MarketEvent[];
  enabled: Record<string, boolean>;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [, setVersion] = useState(0);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<{ ev: MarketEvent; x: number } | null>(null);

  useEffect(() => {
    if (!chart) return;
    const bump = () => setVersion((v) => v + 1);
    chart.timeScale().subscribeVisibleLogicalRangeChange(bump);
    return () => chart.timeScale().unsubscribeVisibleLogicalRangeChange(bump);
  }, [chart]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setWidth(el.clientWidth);
      setVersion((v) => v + 1);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const visible = useMemo(
    () => events.filter((e) => enabled[e.kind] !== false),
    [events, enabled],
  );

  /** Bar open times, ascending — the only x values the time scale knows. */
  const barTimes = useMemo(() => bars.map((b) => b.time), [bars]);

  /** The bar an event belongs to.
   *
   *  `timeToCoordinate` only resolves timestamps that exist on the scale, and
   *  an earnings release at 20:00 never coincides with a daily bar stamped
   *  04:00 — so every marker silently returned null until events were snapped
   *  to the bar containing them.
   */
  const snap = useMemo(() => {
    return (seconds: number): number | null => {
      if (barTimes.length === 0) return null;
      let lo = 0;
      let hi = barTimes.length - 1;
      if (seconds < barTimes[0]!) return null;
      if (seconds >= barTimes[hi]!) return barTimes[hi]!;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (barTimes[mid]! <= seconds) lo = mid;
        else hi = mid - 1;
      }
      return barTimes[lo]!;
    };
  }, [barTimes]);

  const placed = useMemo(() => {
    if (!chart) return [];
    const scale = chart.timeScale();
    const out: { ev: MarketEvent; x: number }[] = [];
    for (const ev of visible) {
      const seconds = Math.floor(new Date(ev.ts).getTime() / 1000);
      const anchored = snap(seconds);
      if (anchored === null) continue;
      const x = scale.timeToCoordinate(anchored as Time);
      if (x === null || x === undefined) continue;
      out.push({ ev, x: x as number });
    }
    // Keep the higher-signal marker when two land on the same pixel column.
    const priority: Record<string, number> = {
      earnings: 3, split: 2, dividend: 1, news: 0,
    };
    const byColumn = new Map<number, { ev: MarketEvent; x: number }>();
    for (const item of out) {
      const col = Math.round(item.x / 11);
      const existing = byColumn.get(col);
      if (
        !existing ||
        (priority[item.ev.kind] ?? 0) > (priority[existing.ev.kind] ?? 0)
      ) {
        byColumn.set(col, item);
      }
    }
    return [...byColumn.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, visible, width, snap]);

  return (
    <div ref={hostRef} className="event-strip" style={{ height: ROW_HEIGHT }}>
      {placed.map(({ ev, x }) => (
        <button
          key={`${ev.kind}-${ev.ts}`}
          className={`event-marker ${ev.tone} ${ev.upcoming ? "upcoming" : ""}`}
          style={{
            left: x,
            // Filled, not outlined. A 15px ring with dim text is a smudge at
            // a glance; a filled disc with a white letter is a marker.
            background: EVENT_COLOR[ev.kind],
            borderColor: EVENT_COLOR[ev.kind],
            color: onColor(EVENT_COLOR[ev.kind] ?? "#64748b"),
          }}
          onMouseEnter={() => setHover({ ev, x })}
          onMouseLeave={() => setHover(null)}
          onClick={() => {
            const url = ev.data?.url;
            if (typeof url === "string" && url) window.open(url, "_blank", "noopener");
          }}
          aria-label={ev.title}
        >
          {ev.label}
        </button>
      ))}

      {hover && (
        <div
          className="event-tip"
          style={{
            // Flip to the left near the right edge so the tooltip stays on screen.
            left: Math.min(Math.max(8, hover.x - 8), Math.max(8, width - 300)),
          }}
        >
          <div className="event-tip-head">
            <span className="badge" style={{ borderColor: EVENT_COLOR[hover.ev.kind] }}>
              {hover.ev.kind}
            </span>
            <strong>{hover.ev.title}</strong>
          </div>
          <div className="faint small">{hover.ev.detail}</div>
          <div className="faint small mono" style={{ marginTop: 3 }}>
            {new Date(hover.ev.ts).toLocaleString(undefined, {
              dateStyle: "medium", timeStyle: "short",
            })}
            {typeof hover.ev.data?.url === "string" && " · click to open"}
          </div>
        </div>
      )}
    </div>
  );
}
